//Overview Dashboard//

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import {
  Users, Building, FolderOpen, TrendingUp, AlertTriangle, Clock, Calendar
} from 'lucide-react';
import MetricCard from './MetricCard';

const OverviewDashboard = ({ stats, dashboardData, userRole }) => {
  // Format trend values
  const formatTrend = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  console.log('📊 OverviewDashboard Received Data:', {
    performanceData: dashboardData.performanceData,
    departmentStats: dashboardData.departmentStats,
    recentTickets: dashboardData.recentTickets,
    recentTicketsLength: dashboardData.recentTickets?.length
  });

  return (
    <div className="space-y-4">
      {/* Key Metrics */}
      <div className={`grid grid-cols-1 ${userRole && userRole !== 'company' && userRole !== 'Manager' ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
        {userRole && userRole !== 'company' && userRole !== 'Manager' ? (
          <>
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              title="My Total Tasks"
              value={stats.totalEmployees}
              color="blue"
            />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Tasks Completed"
              value={stats.totalCustomers}
              color="green"
            />
            <MetricCard
              icon={<FolderOpen className="w-6 h-6" />}
              title="My Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MetricCard
              icon={<Calendar className="w-6 h-6" />}
              title="My Attendance"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        ) : (
          <>
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              title="Total Employees"
              value={stats.totalEmployees}
              color="blue"
            />
            <MetricCard
              icon={<FolderOpen className="w-6 h-6" />}
              title="Active Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Attendance Rate"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        )}
      </div>

      {/* Upcoming Deadlines */}


      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* TOP TASKS PERFORMERS — moved here from Row 2 */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tasks Performers</h3>
          {dashboardData.performanceData.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.performanceData.map((employee, index) => (
                <div key={employee.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{employee.name}</p>
                      <p className="text-sm text-gray-500">{employee.role} - {employee.completed} items completed</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No performance data available</div>
          )}
        </div>

        {/* UPCOMING DEADLINES — moved here from Row 1 left */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {dashboardData.upcomingDeadlines.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
              <div>
                {dashboardData.upcomingDeadlines.map((item, index) => {
                  const deadline = new Date(item.dueDate);
                  const today = new Date();
                  const daysUntil = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        {daysUntil <= 1 ? <AlertTriangle className="w-5 h-5 text-red-500" /> : daysUntil <= 3 ? <Clock className="w-5 h-5 text-yellow-500" /> : <Calendar className="w-5 h-5 text-blue-500" />}
                        <div>
                          <h4 className="font-medium text-gray-900">{item.title || item.name || item.projectName}</h4>
                          <p className="text-sm text-gray-500">{item.type} - {daysUntil === 0 ? 'Due today' : `Due in ${daysUntil} days`}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">{deadline.toLocaleDateString('en-GB')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Performance & Department Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* PROJECT STATUS DISTRIBUTION — moved here from Row 1 right */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Status Distribution</h3>
          {dashboardData.projectStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dashboardData.projectStatus}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {dashboardData.projectStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value}`, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => `${value} (${entry.payload.value})`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">No project data available</div>
          )}
        </div>

        {/* Recent Tickets — unchanged */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Tickets</h3>
          {dashboardData.recentTickets && dashboardData.recentTickets.length > 0 ? (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {dashboardData.recentTickets.slice(0, 4).map((ticket, index) => (
                <div key={ticket._id || index} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm line-clamp-1 flex-1">{ticket.subject || "No Subject"}</h4>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full whitespace-nowrap ${ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                      ticket.status === 'inprogress' ? 'bg-yellow-100 text-yellow-800' :
                        ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          ticket.status === 'closed' ? 'bg-gray-300 text-gray-700' :
                            'bg-orange-100 text-orange-800'
                      }`}>
                      {ticket.status || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center text-xs text-gray-600 flex-1 min-w-0">
                      <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{ticket.customerName || "Unknown"}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-600 flex-1 min-w-0">
                      <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{ticket.department || "N/A"}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${ticket.priority === 'high' ? 'bg-red-100 text-red-800' :
                      ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                      {ticket.priority ? ticket.priority.toUpperCase() : 'LOW'}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500">
              <FolderOpen className="w-12 h-12 mb-2 text-gray-300" />
              <p className="text-sm">No recent tickets available</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default OverviewDashboard;
